import type { Hold } from "@/client/features/maintenance/holds-api";
import type { NodePublicIp } from "@/client/features/nodes/nodes-api";
import { apiFetch } from "@/client/lib/api";
import type { FulfillmentMode, TenantFeature } from "@/shared/features";
import type { PropagationStatus } from "@/shared/propagation";
import type { StaffRole, TenantRole, UserKind } from "@/shared/types";

/** One feature row in the staff Features card: registry metadata + on/off state. */
export interface AdminTenantFeature {
  key: TenantFeature;
  label: string;
  description: string;
  enabled: boolean;
  /** True for features that carry a fulfillment mode (publicPorts, networkAccess). */
  modeCapable: boolean;
  /** The mode for an enabled mode-capable feature; null otherwise. */
  mode: FulfillmentMode | null;
}

/** Staff-only tenant directory + tenant management (create/edit, members, keys). */

export type TenantStatus = "active" | "suspended";

/** Outcome of the NetBox + Key Vault provisioning that runs on tenant creation. */
export type TenantProvisioningStatus = "pending" | "provisioned" | "failed";

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  slackChannelId: string | null;
  slackInternalChannelId: string | null;
  /**
   * Dedicated "inform" channel (the *-inform channel) for bidirectional board
   * communication and automated, low-signal notices (the deploy heads-up). When
   * null, those notices are skipped for this tenant (no fallback to the joint
   * channel).
   */
  slackInformChannelId: string | null;
  /** Deep-link to this tenant in NetBox (filtered by the portal_tenant_id field). */
  netboxUrl: string;
  /** NetBox/Key Vault provisioning outcome (PE-980); `failed` offers a Retry. */
  provisioningStatus: TenantProvisioningStatus;
  /** Last provisioning error, shown to staff when `provisioningStatus` is failed. */
  provisioningError: string | null;
  /**
   * ISO timestamp when the tenant was deactivated (PE-991, Workflow B), or null
   * when active. A deactivated tenant keeps portal access but its self-service
   * write actions + API tokens are disabled.
   */
  deactivatedAt: string | null;
  /** Optional staff note on why the tenant was deactivated; null when active. */
  deactivationReason: string | null;
  /**
   * ISO timestamp when staff armed per-tenant MFA enforcement (the staged-rollout
   * canary), or null when not required. Effective enforcement also requires the
   * environment master switch (`MFA_ENFORCED`) to be on.
   */
  mfaRequiredAt: string | null;
}

/** Result of a hard delete (PE-991, Workflow A). */
export interface HardDeleteTenantResult {
  /** True when a linked NetBox tenant was found and deleted. */
  netboxDeleted: boolean;
  /** True when a Key Vault private key remains and needs a manual purge. */
  keyVaultRetained: boolean;
}

export interface AdminTenantMember {
  id: string;
  email: string;
  name: string | null;
  kind: UserKind;
  tenantId: string | null;
  tenantRole: TenantRole | null;
  staffRole: StaffRole | null;
  status: string;
  mfaEnabled: boolean;
}

export interface AdminTenantSshKey {
  id: string;
  label: string | null;
  keyType: string;
  fingerprint: string;
  comment: string | null;
  createdAt: string;
  userId: string;
  userEmail: string;
  userName: string | null;
}

export interface AdminTenantAllowedIp {
  id: string;
  value: string;
  label: string | null;
  createdByName: string | null;
  createdAt: string;
}

/** A live NetBox node as the staff management view sees it. */
export interface AdminTenantNode {
  netboxDeviceId: number;
  name: string;
  gpuType: string | null;
  status: string | null;
  statusLabel: string | null;
  site: string | null;
  rack: string | null;
  serial: string | null;
  hostname: string | null;
  customerVisible: boolean;
  /** The tenant's public-IP request for this node + its propagation state (PE-920). */
  publicIp: NodePublicIp | null;
}

export interface AdminTenantNodesResponse {
  nodes: AdminTenantNode[];
  netboxConfigured: boolean;
  /** Staff node-management actions are usable (read + write tokens present). */
  writeEnabled: boolean;
  /** This tenant has a NetBox mapping (`portal_tenant_id` set on a tenant). */
  mapped: boolean;
}

/** The allocatable pool - unassigned live nodes (production or maintenance). */
export interface AdminAvailableNodesResponse {
  nodes: AdminTenantNode[];
  netboxConfigured: boolean;
}

/** Legal deallocation targets (clear tenant + set this status). */
export type DeallocationTarget = "production" | "maintenance" | "decommissioned";

export interface AllocateNodesInput {
  deviceIds: number[];
  customerVisible: boolean;
  reason: string;
}

export interface AllocateNodesResult {
  allocated: number[];
  failed: { deviceId: number; error: string }[];
  nodes: AdminTenantNode[];
  netboxConfigured: boolean;
  writeEnabled: boolean;
}

export interface NodeMutationResult {
  nodes: AdminTenantNode[];
  netboxConfigured: boolean;
  writeEnabled: boolean;
}

export interface CreateTenantInput {
  name: string;
  slug: string;
  status: TenantStatus;
  slackChannelId?: string;
  slackInternalChannelId?: string;
  slackInformChannelId?: string;
}

export interface UpdateTenantInput {
  name?: string;
  slug?: string;
  status?: TenantStatus;
  slackChannelId?: string;
  slackInternalChannelId?: string;
  slackInformChannelId?: string;
}

export interface AdminPendingInvite {
  id: string;
  email: string;
  role: TenantRole;
  expiresAt: string;
  createdAt: string;
}

export interface CreateTenantInviteInput {
  email: string;
  role: TenantRole;
}

export interface CreatedTenantInvite {
  id: string;
  email: string;
  role: TenantRole;
  expiresAt: string;
}

export const tenantsAdminApi = {
  list: () => apiFetch<{ tenants: AdminTenant[] }>("/tenants"),
  get: (id: string) => apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}`),
  create: (input: CreateTenantInput) =>
    apiFetch<AdminTenant>("/tenants", { method: "POST", json: input }),
  retryProvision: (id: string) =>
    apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}/provision`, { method: "POST" }),
  update: (id: string, input: UpdateTenantInput) =>
    apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}`, { method: "PATCH", json: input }),
  // Hard delete (PE-991, Workflow A): removes the tenant across NetBox + the
  // portal DB. `confirmSlug` must equal the tenant slug (server re-checks).
  hardDelete: (id: string, confirmSlug: string) =>
    apiFetch<HardDeleteTenantResult>(`/tenants/${encodeURIComponent(id)}`, {
      method: "DELETE",
      json: { confirmSlug },
    }),
  // Deactivate (PE-991, Workflow B): wind a tenant down while keeping its portal
  // access + data. Optional staff-facing reason.
  deactivate: (id: string, reason?: string) =>
    apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}/deactivate`, {
      method: "POST",
      json: reason ? { reason } : {},
    }),
  // Reactivate a deactivated tenant, restoring self-service actions + API tokens.
  reactivate: (id: string) =>
    apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),
  // Arm/disarm per-tenant MFA enforcement (staged-rollout canary). Effective only
  // when the environment master switch (MFA_ENFORCED) is also on.
  setMfaRequired: (id: string, required: boolean) =>
    apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}/mfa-required`, {
      method: "POST",
      json: { required },
    }),
  updateSlackChannel: (id: string, slackChannelId: string) =>
    apiFetch<AdminTenant>(`/tenants/${encodeURIComponent(id)}`, {
      method: "PATCH",
      json: { slackChannelId },
    }),
  members: (id: string) =>
    apiFetch<{ users: AdminTenantMember[] }>(`/users?tenantId=${encodeURIComponent(id)}`),
  resetMemberMfa: (userId: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(userId)}/mfa/reset`, { method: "POST" }),
  sshKeys: (id: string) =>
    apiFetch<{ keys: AdminTenantSshKey[] }>(`/tenants/${encodeURIComponent(id)}/ssh-keys`),
  allowedIps: (id: string) =>
    apiFetch<{ allowedIps: AdminTenantAllowedIp[] }>(
      `/tenants/${encodeURIComponent(id)}/allowed-ips`,
    ),
  holds: (id: string) => apiFetch<{ holds: Hold[] }>(`/tenants/${encodeURIComponent(id)}/holds`),
  nodes: (id: string) =>
    apiFetch<AdminTenantNodesResponse>(`/tenants/${encodeURIComponent(id)}/nodes`),
  availableNodes: (id: string) =>
    apiFetch<AdminAvailableNodesResponse>(`/tenants/${encodeURIComponent(id)}/nodes/available`),
  allocateNodes: (id: string, input: AllocateNodesInput) =>
    apiFetch<AllocateNodesResult>(`/tenants/${encodeURIComponent(id)}/nodes/allocate`, {
      method: "POST",
      json: input,
    }),
  deallocateNode: (
    id: string,
    deviceId: number,
    input: { targetStatus: DeallocationTarget; reason: string },
  ) =>
    apiFetch<NodeMutationResult>(
      `/tenants/${encodeURIComponent(id)}/nodes/${deviceId}/deallocate`,
      { method: "POST", json: input },
    ),
  setNodeVisibility: (id: string, deviceId: number, customerVisible: boolean) =>
    apiFetch<NodeMutationResult>(
      `/tenants/${encodeURIComponent(id)}/nodes/${deviceId}/visibility`,
      { method: "PATCH", json: { customerVisible } },
    ),
  // Staff override of a node's public-IP propagation state (PE-924): verify with
  // the assigned address, or reset to pending/failed. Returns just the updated
  // record so the caller can patch that node in place.
  setNodePublicIpStatus: (
    id: string,
    deviceId: number,
    input: { status: PropagationStatus; publicIp?: string | null },
  ) =>
    apiFetch<{ publicIp: NodePublicIp }>(
      `/tenants/${encodeURIComponent(id)}/nodes/${deviceId}/public-ip`,
      { method: "PATCH", json: input },
    ),
  features: (id: string) =>
    apiFetch<{ features: AdminTenantFeature[] }>(`/tenants/${encodeURIComponent(id)}/features`),
  setFeature: (id: string, feature: TenantFeature, enabled: boolean) =>
    apiFetch<{ features: AdminTenantFeature[] }>(
      `/tenants/${encodeURIComponent(id)}/features/${encodeURIComponent(feature)}`,
      { method: "PATCH", json: { enabled } },
    ),
  setFeatureMode: (id: string, feature: TenantFeature, mode: FulfillmentMode) =>
    apiFetch<{ features: AdminTenantFeature[] }>(
      `/tenants/${encodeURIComponent(id)}/features/${encodeURIComponent(feature)}/mode`,
      { method: "PATCH", json: { mode } },
    ),
  invites: (id: string) =>
    apiFetch<{ invites: AdminPendingInvite[] }>(`/invites?tenantId=${encodeURIComponent(id)}`),
  // Staff admin provisions a user into a specific tenant - notably the first
  // tenant_admin of a brand-new org, which the customer team page can't do
  // (it needs an existing admin). The server gates this on staff-admin.
  createInvite: (id: string, input: CreateTenantInviteInput) =>
    apiFetch<CreatedTenantInvite>("/invites", {
      method: "POST",
      json: { ...input, tenantId: id },
    }),
};
